/**
 * @fileoverview The condition rows a user is editing, incomplete ones included
 *
 * The chain always ends in one incomplete row, where the next condition is typed: completing the
 * last row appends a fresh one, and a last row that becomes incomplete again takes back the empty
 * row behind it. An incomplete row elsewhere stays put while the editor is open, so the focus never
 * jumps; the editor lives only while its popup is open (SCADropdown unmounts closed content), so
 * incomplete rows are gone by the next open.
 */

import type { MaybeRef, MaybeRefOrGetter } from "vue";
import { computed, ref, toValue, unref } from "vue";

import { FilterOperator, LogicalOperator } from "../filterModel";
import type { ConditionRow } from "./conditionChain";
import { completeConditionRows, emptyConditionRow, isConditionRowComplete } from "./conditionChain";
import { isValuelessOperator } from "./operators";
import type { RangeTermCompare } from "./rangeOrder";

export interface ConditionChainOptions {
  /** Operator of every new row. */
  defaultOperator: MaybeRefOrGetter<FilterOperator>;
  /**
   * The column's order of range bounds: a Between whose From lies after its To is not applied.
   * A ref, never a getter: `toValue` would call the comparator itself.
   */
  compare?: MaybeRef<RangeTermCompare | undefined>;
}

export type ConditionTermField = "term" | "termTo";

const hasNoTerms = (row: ConditionRow) => row.term === "" && row.termTo === "";

interface ChainEntry {
  key: number;
  row: ConditionRow;
}

export function useConditionChain(options: ConditionChainOptions) {
  const entries = ref<ChainEntry[]>([]);
  const rows = computed(() => entries.value.map((entry) => entry.row));
  const keys = computed(() => entries.value.map((entry) => entry.key));
  let nextKey = 0;

  const isComplete = (row: ConditionRow) => isConditionRowComplete(row, unref(options.compare));
  // The chain grows on filled terms, not on bound order: typing To across From must not drop and
  // re-add the empty row behind it on every keystroke.
  const isFilled = (row: ConditionRow) => isConditionRowComplete(row);

  function newEntry(join: LogicalOperator): ChainEntry {
    return { key: nextKey++, row: emptyConditionRow(toValue(options.defaultOperator), join) };
  }

  function commit(next: ChainEntry[]) {
    if (next.length === 0) next.push(newEntry(LogicalOperator.AND));
    if (next[0].row.join !== LogicalOperator.AND) {
      next[0] = { ...next[0], row: { ...next[0].row, join: LogicalOperator.AND } };
    }
    const last = () => next[next.length - 1].row;
    while (next.length > 1 && hasNoTerms(last()) && !isFilled(next[next.length - 2].row)) next.pop();
    if (isFilled(last())) next.push(newEntry(LogicalOperator.OR));
    entries.value = next;
  }

  function update(index: number, patch: Partial<ConditionRow>) {
    if (!entries.value[index]) return;
    commit(entries.value.map((entry, i) => (i === index ? { ...entry, row: { ...entry.row, ...patch } } : entry)));
  }

  function setOperator(index: number, operator: FilterOperator) {
    const row = rows.value[index];
    if (!row) return;
    const valueless = isValuelessOperator(operator);
    update(index, {
      operator,
      term: valueless ? "" : row.term,
      termTo: operator === FilterOperator.IN_RANGE ? row.termTo : "",
    });
  }

  function setTerm(index: number, value: string, field: ConditionTermField = "term") {
    update(index, { [field]: value });
  }

  function setJoin(index: number, join: LogicalOperator) {
    if (index > 0) update(index, { join });
  }

  function removeRow(index: number) {
    commit(entries.value.filter((_, i) => i !== index));
  }

  function replaceAll(next: readonly ConditionRow[]) {
    commit(next.map((row) => ({ key: nextKey++, row: { ...row } })));
  }

  /** What the filter is made of: the complete rows, the first one unjoined. */
  const completeRows = computed(() => completeConditionRows(rows.value, unref(options.compare)));

  const focusIndex = computed(() =>
    Math.max(
      0,
      rows.value.findIndex((row) => !isComplete(row)),
    ),
  );

  commit([]);

  return {
    rows,
    keys,
    completeRows,
    focusIndex,
    setOperator,
    setTerm,
    setJoin,
    removeRow,
    replaceAll,
  };
}
